import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@firecms/ui";

import { useEventTeamLeaderboard } from "../hooks/useEventLeaderboard";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";
import { RankingRefreshBar } from "./RankingRefreshBar";
import SearchBar from "./SearchBar";
import {
  statsMuted,
  statsPageTitle,
} from "./statsViewTheme";

type TeamRankingViewProps = {
  eventId: EventCollectionId;
};

export function TeamRankingView({ eventId }: TeamRankingViewProps) {
  const { t, locale } = useAdminTranslation();
  const { rows, meta, loading, error } = useEventTeamLeaderboard(eventId);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [eventId.accountId, eventId.eventId]);

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return rows;
    }
    const q = search.toLowerCase();
    return rows.filter((row) => String(row.teamNumber).includes(q));
  }, [rows, search]);

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className={statsPageTitle}>{t("eventRanking.teamTitle")}</h1>
            <p className={`mt-2 text-sm ${statsMuted}`}>
              {t("eventRanking.teamDescription")}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <RankingRefreshBar
            eventId={eventId}
            ranksUpdatedAt={meta?.ranksUpdatedAt ?? 0}
            locale={locale}
            t={t}
            callableName="refreshEventTeamLeaderboard"
          />
        </div>

        <div className="mt-6">
          <SearchBar
            onSearch={setSearch}
            placeholder={t("eventRanking.searchTeam")}
          />
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        ) : null}

        {!loading && error ? (
          <p className={`mt-6 text-sm ${statsMuted}`}>{error}</p>
        ) : null}

        {!loading && !error && filtered.length === 0 ? (
          <p className={`mt-6 text-sm ${statsMuted}`}>
            {t("eventRanking.emptyTeam")}
          </p>
        ) : null}

        {!loading && !error && filtered.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-surface-100 dark:bg-surface-800">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colRank")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colTeamNumber")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("eventRanking.colTeamScore")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.teamId}
                    className="border-t border-surface-200 dark:border-surface-700"
                  >
                    <td className="px-3 py-2 tabular-nums">
                      {row.rank > 0 ? row.rank : "—"}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{row.teamNumber}</td>
                    <td className="px-3 py-2 tabular-nums">{row.teamScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
